import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ContextService } from '@core/services/core/context.service';
import { PaymentOperation } from '@models/operation-register';
import { PendingReceipt, PendingReceiptProductType, ReceiptPaymentErrorType, TpvPaymentType } from '@models/receipt';
import { RedsysInfo } from '@models/redsys';
import { getRedsysFormPage } from '@utils/redsys';
import { BehaviorSubject, of, throwError } from 'rxjs';
import { catchError, map, share, switchMap, tap } from 'rxjs/operators';
import { CustomNotificationId } from '../../models/notification-pg';
import {
  Base64File,
  PaymentPlan,
  SimulationPlan
} from '../../models/payment-plan';
import { PoliciesContextService } from '../context/policies-context.service';
import { AuthMirrorService } from '../core/auth/auth-mirror.service';
import { BaseService } from '../core/base.service';
import { OperationRegisterService } from '../core/operation-register.service';
import { NotificationHelperService } from '../helpers/notification-helper.service';
import { ReceiptPaymentHelperService } from '../helpers/receipt-payment-helper.service';

export enum PaymentPlanAccess {
  NORMAL,
  SUSCEPTIBLE,
  NOT_SUSCEPTIBLE
}
@Injectable({
  providedIn: 'root'
})
export class PaymentPlanService {
  isPendingFee = new BehaviorSubject<boolean>(false);
  private showPaymentPlanSource = new BehaviorSubject<boolean>(false);
  private paymentPlanInformation = new BehaviorSubject<PaymentPlan[] | 'error'>(null);


  constructor(
    private http: HttpClient,
    private or: OperationRegisterService,
    private base: BaseService,
    private notificationHelper: NotificationHelperService,
    private ctx: ContextService,
    private receiptPaymentHelperService: ReceiptPaymentHelperService,
    private authMirror: AuthMirrorService,
    private policiesCtx: PoliciesContextService
  ) {}

  get isPendingFee$() {
    return this.isPendingFee.asObservable();
  }

  get showPaymentPlanAccessOnHomePage$() { return this.showPaymentPlanSource.asObservable().pipe(share()); }


  get paymentPlanInformation$() { return this.paymentPlanInformation; }

  clear() {
    this.isPendingFee.next(false);
    this.showPaymentPlanSource.next(false);
    this.paymentPlanInformation.next(null);
  }

  updatePaymentPlanAccessOnHomeState(status: boolean) {
    this.showPaymentPlanSource.next(status);
  }

  getPaymentPlanAccess() {
    return this.getPaymentPlans().pipe(
      switchMap(plans =>
        plans.length
          ? of({
              access: PaymentPlanAccess.NORMAL,
              susceptiblePolicyNumbers: []
            })
          : this.isSusceptible()
      ), catchError(() => of({
        access: PaymentPlanAccess.NORMAL,
        susceptiblePolicyNumbers: []
      }))
    );
  }

  getPaymentPlansDetail(opts?: { clearCache?: boolean }) {
    const obtainDetail = true;
    const url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans?obtainDetail=${obtainDetail}`;
    const params =
      opts && opts.clearCache
        ? this.or.setPaymentPlanOperationParams().append('forceRefresh', 'true')
        : this.or.setPaymentPlanOperationParams();

    return this.http
      .get<PaymentPlan[]>(url, {
        ...this.base.getPrivateOIMHeaders(),
        params
      })
      .pipe(
        map(paymentPlans =>
          this.setPaymentPlansReceiptsMmobileInfo(paymentPlans)
        ),
        tap(
          paymentPlans => this.updatePaymentPlanInformation(paymentPlans),
          () => this.updatePaymentPlanInformation('error')
        )
      );
  }

  getPaymentPlansDocument(
    { paymentPlanId, id },
    documentType: 'contract' | 'summary' | 'receipt'
  ) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans/${paymentPlanId}/documents/${documentType}/${id}`;
    return this.http
      .get<Base64File>(url, {
        ...this.base.getPrivateOIMHeaders(),
        params: this.or.setPaymentPlanOperationParams({
          productId: paymentPlanId
        })
      })
      .pipe(
        switchMap(file =>
          file.base64Content
            ? of({
                name: id,
                mimeType: 'application/pdf',
                ...file
              })
            : throwError('base64Content missing')
        )
      );
  }

  // This endpoint will be replaced by the getSimulationPlan() one at some point
  getSimulation() {
    const url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans/documents/simulation`;
    return this.http
      .get<Base64File>(url, {
        ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
        params: this.or.setPaymentPlanOperationParams({
          productId: 'Plan de Pagos'
        })
      })
      .pipe(
        map(file => ({
          name: 'Simulación Plan de Pago',
          mimeType: 'application/pdf',
          ...file
        }))
      );
  }

  getSimulationPlan(paymentMonthDay: number) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans/simulation`;
    const params = this.or
      .setPaymentPlanOperationParams({
        productId: 'Plan de Pagos'
      })
      .append('paymentMonthDay', paymentMonthDay.toString());
    const headers = {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true })
    };
    return this.http.get<SimulationPlan>(url, { ...headers, params }).pipe(
      map(plan => ({
        name: 'Simulación Plan de Pago',
        mimeType: 'application/pdf',
        ...plan
      }))
    );
  }

  getRedsysInfoForPayment() {
    const url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans/redsys/prepareData`;
    const params = this.or
      .setPaymentPlanOperationParams({
        productId: 'Plan de Pagos'
      });
    const headers = {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true })
    };
    return this.http.get<RedsysInfo>(url, { ...headers, params });
  }

  confirmRedsysPayment(body: { parameters: string, signature: string}) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans/redsys/paymentConfirmation`;
    const params = this.or
      .setPaymentPlanOperationParams({
        productId: 'Plan de Pagos'
      });
    const headers = {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true })
    };
    return this.http.post<void>(url, body, { ...headers, params });
  }

  processSimulationPlanContract(iban: string, paymentMonthDay: number, redsysData?: string) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans`;
    const params = this.or
      .setPaymentPlanOperationParams({
        productId: 'Plan de Pagos'
      })
      .append('paymentMonthDay', paymentMonthDay.toString());
    const headers = {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true })
    };
    const requestBody = JSON.stringify({ iban, redsysData });

    return this.http
      .post<Base64File>(url, requestBody, { ...headers, params })
      .pipe(
        map(file => ({
          name: 'Contrato Plan de Pagos sin firmar',
          mimeType: 'application/pdf',
          ...file
        }))
      );
  }

  sendCallMeBack(body: { phone; initTime; campaign }) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans/callMeLater`;
    return this.http.post<any>(url, body, {
      ...this.base.getPrivateOIMHeaders(),
      params: this.or.setPaymentPlanOperationParams({
        productId: 'Plan de Pagos'
      })
    });
  }

  isPolicyInPaymentPlan(paymentPlans: PaymentPlan[], policy: string) {
    return !!paymentPlans.filter(
      ({ policyNumbers }) =>
        policyNumbers &&
        policyNumbers.some(
          paymentPolicy =>
            paymentPolicy.includes(policy) || policy.includes(paymentPolicy)
        )
    ).length;
  }

  checkIsPendingFee() {
    this.getPaymentPlansDetail()
      .pipe(
        tap((paymentPlan: PaymentPlan[]) => this.updatePaymentPlanAccessOnHomeState(!!paymentPlan.length)),
        map((paymentPlan: PaymentPlan[]) => {
          return paymentPlan
            .map(({ paymentPendingFees }) => paymentPendingFees || [])
            .some(paymentPendingFee => !!paymentPendingFee.length);
        }),
        tap(isPendingFee => {
          const pendingFeeNotification =
            CustomNotificationId.NOTIFICATION_PAYMENT_PLAN;
          if (isPendingFee) {
            this.notificationHelper.updateCustomNotification({
              id: pendingFeeNotification
            });
          } else {
            this.notificationHelper.close(pendingFeeNotification);
          }
        }),
        catchError(() => {
          this.updatePaymentPlanAccessOnHomeState(false);
          return of(false); })
      )
      .subscribe(isPendingFee => {
        this.isPendingFee.next(isPendingFee);
      });
  }

  getFeeDetailByNumber(
    receiptData: {
      receiptNumber: string;
      departmentType: string;
      department: string;
      description: string;
      type: PendingReceiptProductType;
      policyNumber?: string;
    },
    documentNumber?: string
  ) {
    const appIds = this.receiptPaymentHelperService.getAppIds();
    const receiptType = receiptData.type ? receiptData.type : PendingReceiptProductType.GENERAL;
    if (receiptType === PendingReceiptProductType.LIFE) {
      if (!this.receiptPaymentHelperService.checkLifeServicesAreAvailable()) {
        const orStructure = { receiptNumber: receiptData.receiptNumber } as any;
        if (receiptData.policyNumber) {
          orStructure.policyNumber = receiptData.policyNumber;
        }
        this.or.registerPrivatePaymentOperation(
          receiptData.departmentType,
          receiptData.department,
          PaymentOperation.SERVICIO_VIDA_FUERA_DE_HORARIO_PRIVADA,
          JSON.stringify(orStructure)
        );
        return throwError(
          this.receiptPaymentHelperService.getServiceErrorObjectByErrorKey(ReceiptPaymentErrorType.LIFE_SERVICES_NOT_AVAILABLE)
        );
      }
    }
    const { isLogged } = this.authMirror.authService.getState();
    const url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans/receipts/${
      receiptData.receiptNumber
    }?type=${receiptType}&enablePaymentsPlan=true&appId=${
      appIds.appId
    }&amazonPayAppId=${appIds.amazonPayAppId}&amazonPayOrigin=${
      appIds.amazonPayOrigin
    }&amazonPayPortal=${appIds.amazonPayPortal}&privateArea=${isLogged}`;
    const headers = this.base.getPublicHeaders();
    const params = this.or.setPaymentOperationParams(
      receiptData.departmentType,
      receiptData.department,
      receiptData.description,
      documentNumber
    );
    return this.http
      .get<PendingReceipt>(url, { ...headers, params })
      .pipe(
        map(receipt => {
          const receiptDetail = this.receiptPaymentHelperService.setReceiptDepartmentInfoFromMMobile(receipt);
          if (receiptDetail) {
            return receiptDetail;
          } else {
            throw {
              locallyGeneratedError: true,
              errorKey: ReceiptPaymentErrorType.RECEIPT_PAYMENT_NOT_ADMITTED,
              errorTranslationKey: this.receiptPaymentHelperService.resolveReceiptPaymentErrorTranslationKey(
                ReceiptPaymentErrorType.RECEIPT_PAYMENT_NOT_ADMITTED
              ),
              receiptData: receipt
            };
          }
        }),
        catchError(err => {
          if (err && err.locallyGeneratedError) {
            return throwError(err);
          }
          const localError: any = this.receiptPaymentHelperService.getServiceErrorObject(err);
          if (
            (localError && localError.errorKey === ReceiptPaymentErrorType.RECEIPT_PAYMENT_NOT_PENDING)
            && err?.error?.extraData?.receipt
          ) {
              const receiptWithResolvedDepartmentType = this.receiptPaymentHelperService.setReceiptDepartmentInfoFromMMobile(
                err.error.extraData.receipt
              );
              if (receiptWithResolvedDepartmentType) {
                localError.receiptData = receiptWithResolvedDepartmentType;
              } else {
                localError.receiptData = err.error.extraData.receipt;
              }
            }
          return throwError(localError);
        })
      );
  }

  // Paypal Fee Payment

  getPaypalPageUrlForFeePayment(
    receipt: PendingReceipt,
    roParams: any
  ) {
    const appIds = this.receiptPaymentHelperService.getAppIds();
    const remissionAppId = this.receiptPaymentHelperService.getRemissionAppId(receipt);
    const { isLogged } = this.authMirror.authService.getState();
    // enablePaymentsPlan fixed to true. Now is allowed to pay Payment Plans receipts in Private area (true)
    // if the receipt is not a Payment Plan receipt, the service dont mind this field
    const url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans/${
      receipt.number
    }/paypal/prepareData?description=${
      receipt.description
    }&appId=${
      appIds.appId
    }&remissionAppId=${remissionAppId}&amazonPayAppId=${
      appIds.amazonPayAppId
    }&amazonPayOrigin=${appIds.amazonPayOrigin}&amazonPayPortal=${
      appIds.amazonPayPortal
    }&privateArea=${isLogged}`;
    const headers = this.base.getPublicHeaders();
    const params = this.or.setPaymentOperationParams(
      receipt.departmentType,
      receipt.policyDepartment,
      receipt.description,
      roParams.documentNumber
    );
    return this.http
      .get<any>(url, { ...headers, params })
      .pipe(
        catchError(err => {
          return throwError(this.receiptPaymentHelperService.getServiceErrorObject(err));
        })
      );
  }

  confirmPaypalFeePayment(
    receipt: PendingReceipt,
    token: string,
    roParams: any
  ) {
    const appIds = this.receiptPaymentHelperService.getAppIds();
    const remissionAppId = this.receiptPaymentHelperService.getRemissionAppId(receipt);
    const { isLogged } = this.authMirror.authService.getState();
    // tslint:disable-next-line:max-line-length
    const url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans/${
      receipt.number
    }/paypal/paymentConfirmation?paypalToken=${token}&appId=${
      appIds.appId
    }&remissionAppId=${remissionAppId}&amazonPayAppId=${
      appIds.amazonPayAppId
    }&amazonPayOrigin=${appIds.amazonPayOrigin}&amazonPayPortal=${
      appIds.amazonPayPortal
    }&privateArea=${isLogged}`;
    const headers = this.base.getPublicHeaders({
      cacheResponse: false,
      clearCache: true
    });
    this.policiesCtx.clear();
    const params = this.or.setPaymentOperationParams(
      receipt.departmentType,
      receipt.policyDepartment,
      receipt.description,
      roParams.documentNumber
    );
    return this.http.post<any>(url, {}, { ...headers, params }).pipe(
      catchError(err => {
        return throwError(this.receiptPaymentHelperService.getServiceErrorObject(err));
      })
    );
  }

  // Redsys Fee Payment

  getParamsForFeePayment(
    receipt: PendingReceipt,
    roParams: any,
    paymentType: TpvPaymentType
  ) {
    return this.getTpvParams(receipt, roParams, paymentType).pipe(
      catchError(err => {
        return throwError(this.receiptPaymentHelperService.getServiceErrorObject(err));
      })
    );
  }

  private getTpvParams(
    receipt: PendingReceipt,
    roParams: any,
    paymentType: TpvPaymentType
  ) {
    const appIds = this.receiptPaymentHelperService.getAppIds();
    const remissionAppId = this.receiptPaymentHelperService.getRemissionAppId(receipt);
    const { isLogged } = this.authMirror.authService.getState();

    let url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans/${
      receipt.number
    }/${paymentType}/prepareData?appId=${
      appIds.appId
    }&remissionAppId=${remissionAppId}&amazonPayAppId=${
      appIds.amazonPayAppId
    }&amazonPayOrigin=${appIds.amazonPayOrigin}&amazonPayPortal=${
      appIds.amazonPayPortal
    }&privateArea=${isLogged}`;
    url += `&group=${receipt.redsysAgrup ? receipt.redsysAgrup : ''}`;

    const headers = this.base.getPublicHeaders();
    const params = this.or.setPaymentOperationParams(
      receipt.departmentType,
      receipt.policyDepartment,
      receipt.description,
      roParams.documentNumber,
      roParams.email,
    );
    return this.http
      .get<RedsysInfo>(url, { ...headers, params })
      .pipe(
        map(info => {
          info.form = getRedsysFormPage(info);
          return info;
        }),
        catchError(err => {
          return throwError(this.receiptPaymentHelperService.getServiceErrorObject(err));
        })
      );
  }


  confirmRedsysFeePayment(
    receipt: PendingReceipt,
    resultDetails: any,
    commerceName: string,
    roParams: any,
    paymentType: TpvPaymentType,
  ) {
    const appIds = this.receiptPaymentHelperService.getAppIds();
    const remissionAppId = this.receiptPaymentHelperService.getRemissionAppId(receipt);
    const { isLogged } = this.authMirror.authService.getState();
    // enablePaymentsPlan fixed to true. Now is allowed to pay Payment Plans receipts in Private area (true)
    // if the receipt is not a Payment Plan receipt, the service dont mind this field
    let url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans/${
      receipt.number
    }/${paymentType}/paymentConfirmation?appId=${
      appIds.appId
    }&amazonPayAppId=${
      appIds.amazonPayAppId
    }&amazonPayOrigin=${appIds.amazonPayOrigin}&amazonPayPortal=${
      appIds.amazonPayPortal
    }&privateArea=${isLogged}`;
    url += `&group=${receipt.redsysAgrup ? receipt.redsysAgrup : ''}`;

    if (paymentType !== 'bizum'){
      url += `&remissionAppId=${remissionAppId}`;
    }

    const requestBody = {
      commerceName,
      parameters: resultDetails.Ds_MerchantParameters,
      signature: resultDetails.Ds_Signature
    };
    const headers = this.base.getPublicHeaders({
      cacheResponse: false,
      clearCache: true
    });
    this.policiesCtx.clear();
    const params = this.or.setPaymentOperationParams(
      receipt.departmentType,
      receipt.policyDepartment,
      receipt.description,
      roParams.documentNumber,
      roParams.email,
      paymentType
    );
    return this.http
      .post<any>(url, requestBody, { ...headers, params })
      .pipe(
        catchError(err => {
          return throwError(this.receiptPaymentHelperService.getServiceErrorObject(err));
        })
      );
  }

  // Amazon Fee payment

  getAmazonPageUrlForFeePayment(
    receipt: PendingReceipt,
    roParams: any,
  ) {
    const appIds = this.receiptPaymentHelperService.getAppIds();
    const remissionAppId = this.receiptPaymentHelperService.getRemissionAppId(receipt);
    const { isLogged } = this.authMirror.authService.getState();
    // enablePaymentsPlan fixed to true. Now is allowed to pay Payment Plans receipts in Private area (true)
    // if the receipt is not a Payment Plan receipt, the service dont mind this field
    const url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans/${
      receipt.number
    }/amazonPay/prepareData?description=${
      receipt.description
    }&appId=${
      appIds.appId
    }&remissionAppId=${remissionAppId}&amazonPayAppId=${
      appIds.amazonPayAppId
    }&amazonPayOrigin=${appIds.amazonPayOrigin}&amazonPayPortal=${
      appIds.amazonPayPortal
    }&privateArea=${isLogged}`;

    const headers = this.base.getPublicHeaders();
    const params = this.or.setPaymentOperationParams(
      receipt.departmentType,
      receipt.policyDepartment,
      receipt.description,
      roParams.documentNumber
    );
    return this.http
      .get<any>(url, { ...headers, params })
      .pipe(
        catchError(err => {
          return throwError(this.receiptPaymentHelperService.getServiceErrorObject(err));
        })
      );
  }

  confirmAmazonFeePayment(
    receipt: PendingReceipt,
    sessionId: string,
    resultToValidate: any,
    roParams: any
  ) {
    const appIds = this.receiptPaymentHelperService.getAppIds();
    const remissionAppId = this.receiptPaymentHelperService.getRemissionAppId(receipt);
    const { isLogged } = this.authMirror.authService.getState();
    // enablePaymentsPlan fixed to true. Now is allowed to pay Payment Plans receipts in Private area (true)
    // if the receipt is not a Payment Plan receipt, the service dont mind this field
    const url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans/${
      receipt.number
    }/amazonPay/paymentConfirmation?appId=${
      appIds.appId
    }&remissionAppId=${remissionAppId}&amazonPayAppId=${
      appIds.amazonPayAppId
    }&amazonPayOrigin=${appIds.amazonPayOrigin}&amazonPayPortal=${
      appIds.amazonPayPortal
    }&privateArea=${isLogged}`;

    const headers = this.base.getPublicHeaders({
      cacheResponse: false,
      clearCache: true
    });
    this.policiesCtx.clear();
    const params = this.or.setPaymentOperationParams(
      receipt.departmentType,
      receipt.policyDepartment,
      receipt.description,
      roParams.documentNumber
    );
    return this.http
      .post<any>(
        url,
        {
          data: resultToValidate,
          sessionId
        },
        { ...headers, params }
      )
      .pipe(
        catchError(err => {
          return throwError(this.receiptPaymentHelperService.getServiceErrorObject(err));
        })
      );
  }



  private isSusceptible() {
    const url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans/susceptibles`;
    return this.http
      .get<any>(url, {
        ...this.base.getPrivateOIMHeaders(),
        params: this.or.setPaymentPlanOperationParams({
          productId: 'Plan de Pagos'
        })
      })
      .pipe(
        catchError(() => of({ susceptible: false, policies: [] })),
        map(({ policies, susceptible }) => ({
          susceptiblePolicyNumbers: (policies || [])
            .filter(({ eligible }) => eligible)
            .map(({ policyNumber }) => policyNumber),
          access: susceptible
            ? PaymentPlanAccess.SUSCEPTIBLE
            : PaymentPlanAccess.NOT_SUSCEPTIBLE
        }))
      );
  }

  private getPaymentPlans() {
    const obtainDetail = false;
    const url = `${this.base.getPrivateOIMBaseUrl()}/receiptsPayment/paymentPlans?obtainDetail=${obtainDetail}`;
    const params = this.or
      .setPaymentPlanOperationParams()
      .append('forceRefresh', 'true');
    return this.http
      .get<any>(url, {
        ...this.base.getPrivateOIMHeaders(),
        params
      });
  }

  private setPaymentPlansReceiptsMmobileInfo(paymentPlans: PaymentPlan[]) {
    return paymentPlans.map(paymentPlan => {
      return {
        ...paymentPlan,
        paymentPendingFees:
          paymentPlan.paymentPendingFees &&
          paymentPlan.paymentPendingFees.map(receipt =>
            this.receiptPaymentHelperService.setReceiptDepartmentInfoFromMMobile(
              receipt
            )
          )
      };
    });
  }

  private updatePaymentPlanInformation(paymentPlan: PaymentPlan[] | 'error') {
    this.paymentPlanInformation.next(paymentPlan);
  }
}
